import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Icon,
} from '@mui/material';
import { Warning, Delete } from '@mui/icons-material';

/**
 * Modern Confirmation Dialog Component
 * Replaces window.confirm() with a beautiful Material-UI dialog
 */
const ConfirmDialog = ({
  open = false,
  title = 'Confirm Action',
  message = 'Are you sure?',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  onConfirm = () => {},
  onCancel = () => {},
  isLoading = false,
  type = 'delete', // 'delete', 'warning', 'info'
  isDangerous = false,
}) => {
  const getIconColor = () => {
    if (isDangerous || type === 'delete') return '#dc2626';
    if (type === 'warning') return '#ea580c';
    return '#2563eb';
  };

  const getButtonColor = () => {
    if (isDangerous || type === 'delete') return '#dc2626';
    if (type === 'warning') return '#ea580c';
    return '#2563eb';
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 2,
          fontWeight: '600',
          color: '#1a202c',
          fontSize: '1.25rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: isDangerous || type === 'delete' ? '#fee2e2' : type === 'warning' ? '#fed7aa' : '#dbeafe',
          }}
        >
          {isDangerous || type === 'delete' ? (
            <Delete sx={{ color: getIconColor(), fontSize: 20 }} />
          ) : (
            <Warning sx={{ color: getIconColor(), fontSize: 20 }} />
          )}
        </Box>
        <span>{title}</span>
      </DialogTitle>

      <DialogContent sx={{ py: 3, color: '#667085' }}>
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          display: 'flex',
          gap: 2,
          p: 3,
          pt: 2,
          justifyContent: 'flex-end',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <Button
          onClick={onCancel}
          disabled={isLoading}
          variant="outlined"
          sx={{
            color: '#667085',
            borderColor: '#d1d5db',
            textTransform: 'none',
            fontWeight: '500',
            px: 3,
            '&:hover': {
              backgroundColor: '#f3f4f6',
              borderColor: '#9ca3af',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          variant="contained"
          sx={{
            backgroundColor: getButtonColor(),
            textTransform: 'none',
            fontWeight: '500',
            px: 3,
            '&:hover': {
              backgroundColor: isDangerous || type === 'delete' ? '#b91c1c' : type === 'warning' ? '#c2410c' : '#1d4ed8',
            },
          }}
        >
          {isLoading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
